import React, { useState, useEffect, useCallback, useRef } from "react";
import TrailersGrid from "./Grid/Trailers";
import ShipmentsGrid from "./Grid/Shipments";
import { withStyles } from "@material-ui/core";
import { compose } from "recompose";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { withSnackbar } from "notistack";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import appConstants from "../../../../appConstants";
import Events from "../../../../utilities/EventEmitter";
import { STRIP_PRIORITY_GRID_TYPE } from "./constants";
import {
  appendTimeInMinutesToDate,
  buildStripPriorityQueryString,
  sortStripPriorityTrailers,
  sortStripPriorityShipments,
} from "./utils";

const styles = (theme) => ({
  visible: {
    display: "block",
  },
  hidden: {
    display: "none",
  },
});

/**
 * @see docs/features/STRIP_PRIORITY_UNIFIED_API_UI.md
 */
const TrailerStripPriority = ({
  hideReportsListItem,
  classes,
  userData,
  enqueueSnackbar,
}) => {
  const [maximizedGrid, setMaximizedGrid] = useState(null);
  const [trailersData, setTrailersData] = useState(null);
  const [shipmentsData, setShipmentsData] = useState(null);
  const [stripPriorityGridStatus, setStripPriorityGridStatus] = useState(
    appConstants.GRID_STATUS.LOADING
  );
  const mountedRef = useRef(true);
  const enqueueSnackbarRef = useRef(enqueueSnackbar);

  useEffect(() => {
    enqueueSnackbarRef.current = enqueueSnackbar;
  }, [enqueueSnackbar]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const userProfile = userData?.userProfile;
  const planDate = userProfile?.planDate;
  const terminalId = userProfile?.terminal?.id;
  const defaultDockStartTimeInMins = userProfile?.defaultDockStartTimeInMins ?? 0;

  const handleStripPriorityError = useCallback(() => {
    enqueueSnackbarRef.current("Unable to fetch strip priority data", {
      variant: "error",
    });
    setTrailersData([]);
    setShipmentsData([]);
    setStripPriorityGridStatus(appConstants.GRID_STATUS.ERROR);
  }, []);

  const fetchStripPriorityData = useCallback(() => {
    if (!planDate || terminalId == null) {
      return;
    }
    const dockStartTimeIso = appendTimeInMinutesToDate(
      planDate,
      defaultDockStartTimeInMins
    );
    setTrailersData(null);
    setShipmentsData(null);
    setStripPriorityGridStatus(appConstants.GRID_STATUS.LOADING);
    const qs = buildStripPriorityQueryString(
      dockStartTimeIso,
      planDate,
      terminalId
    );
    getDataWithQueryString("GetStripPriorityData", qs).then(
      (response) => {
        if (!mountedRef.current) {
          return;
        }
        if (response.status === 200) {
          const payload = response.data || {};
          setTrailersData(sortStripPriorityTrailers(payload.trailers));
          setShipmentsData(sortStripPriorityShipments(payload.shipments));
          setStripPriorityGridStatus(appConstants.GRID_STATUS.SUCCESS);
          Events.emit("ResetRefreshBarTimer-StripPriority");
          return;
        }
        handleStripPriorityError();
      },
      () => {
        if (mountedRef.current) {
          handleStripPriorityError();
        }
      }
    );
  }, [
    planDate,
    terminalId,
    defaultDockStartTimeInMins,
    handleStripPriorityError,
  ]);

  useEffect(() => {
    if (!planDate || terminalId == null) {
      return;
    }
    fetchStripPriorityData();
  }, [planDate, terminalId, defaultDockStartTimeInMins, fetchStripPriorityData]);

  const toggleMaximizeGrid = (gridType) => {
    setMaximizedGrid((prev) => {
      const newValue = prev === gridType ? null : gridType;
      hideReportsListItem(newValue === null);
      return newValue;
    });
  };

  return (
    <>
      <div
        className={
          maximizedGrid === STRIP_PRIORITY_GRID_TYPE.SHIPMENTS
            ? classes.hidden
            : classes.visible
        }
      >
        <TrailersGrid
          toggleMaximize={() =>
            toggleMaximizeGrid(STRIP_PRIORITY_GRID_TYPE.TRAILERS)
          }
          isMaximized={maximizedGrid === STRIP_PRIORITY_GRID_TYPE.TRAILERS}
          trailersData={trailersData}
          stripPriorityGridStatus={stripPriorityGridStatus}
          onRefreshStripPriority={fetchStripPriorityData}
        />
      </div>
      <div
        className={
          maximizedGrid === STRIP_PRIORITY_GRID_TYPE.TRAILERS
            ? classes.hidden
            : classes.visible
        }
      >
        <ShipmentsGrid
          toggleMaximize={() =>
            toggleMaximizeGrid(STRIP_PRIORITY_GRID_TYPE.SHIPMENTS)
          }
          isMaximized={maximizedGrid === STRIP_PRIORITY_GRID_TYPE.SHIPMENTS}
          shipmentsData={shipmentsData}
          stripPriorityGridStatus={stripPriorityGridStatus}
        />
      </div>
    </>
  );
};

export default compose(
  withStyles(styles),
  withUserProfile,
  withSnackbar
)(TrailerStripPriority);
